import { FC } from "react";
import { useTranslation } from "react-i18next";
import RangeSlider from "../../components/range-slider";
import {
  MEDIA_OPTIONS,
  STEP_LABELS_OPTIONS,
  STEP_VALUES,
} from "../../constants";
import {
  AdminFormFieldCheckbox,
  AdminFormFieldDropdown,
  AdminFormFieldInput,
} from "../../../../components/admin/formFields";
import { MASS_CONFIG_DEFAULTS } from "./mass-config-defaults";

interface EventFieldProps {
  values: any;
  errors: any;
  touched: any;
  handleChange: (event: React.ChangeEvent<any>) => void;
  setFieldValue: (field: string, value: any) => void;
  handleBlur: (event: React.FocusEvent<any>) => void;
  setFieldTouched: (field: string, isTouched: boolean) => void;
  selectedField: string;
  massConfigValues: {
    adas: typeof MASS_CONFIG_DEFAULTS | null;
    dms: typeof MASS_CONFIG_DEFAULTS | null;
  };
  setMassConfigValues: React.Dispatch<
    React.SetStateAction<{
      adas: typeof MASS_CONFIG_DEFAULTS | null;
      dms: typeof MASS_CONFIG_DEFAULTS | null;
    }>
  >;
}

const EventField: FC<EventFieldProps> = ({
  values,
  errors,
  touched,
  handleChange,
  setFieldValue,
  handleBlur,
  setFieldTouched,
  selectedField,
  massConfigValues,
  setMassConfigValues
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "camera.dashboard.management.ai_event_detection",
  });
  const { t: tMain } = useTranslation("translation", {
    keyPrefix: "camera.dashboard.management",
  });

  const eventKey = selectedField
    ?.replace(/^adas_/, "")
    .replace(/^dms_/, "")
    .replace(/Enable$/, "");

  const sensitiveField = `${eventKey}_sensitive`;
  const videoBeforeField = `${eventKey}_videoBefore`;
  const videoAfterField = `${eventKey}_videoAfter`;
  const audioAlertField = `${eventKey}_audioAlert`;
  const mediaField = `${eventKey}_media`;
  const eventDebounceField = `${eventKey}_eventDebounce`;

  const isAdasMassConfig = selectedField === "adasEnable";
  const isDmsMassConfig = selectedField === "dmsEnable";
  const isMassConfigure = isAdasMassConfig || isDmsMassConfig;

  const massConfig = isAdasMassConfig
    ? massConfigValues.adas ?? MASS_CONFIG_DEFAULTS
    : isDmsMassConfig
      ? massConfigValues.dms ?? MASS_CONFIG_DEFAULTS
      : null;

  const updateMassConfig = (
    field: keyof typeof MASS_CONFIG_DEFAULTS,
    value: unknown
  ) => {
    const configType = isAdasMassConfig ? "adas" : isDmsMassConfig ? "dms" : null;
    if (!configType) return;

    setMassConfigValues((prev) => ({
      ...prev,
      [configType]: {
        ...(prev[configType] ?? MASS_CONFIG_DEFAULTS),
        [field]: value,
      },
    }));
  };
  const massGroup = selectedField === "adasEnable" ? "adas" : "dms";
  const getError = (field: string, eventField: string) =>
    isMassConfigure
      ? ((errors as Record<string, any>)[`mass_${massGroup}_${field}`] as string | undefined)
      : ((errors as Record<string, any>)[eventField] as string | undefined);

  return (
    <>
      <div className="col-span-6 p-6 border-b space-y-1">
        <div className="mb-4">
          <h1 className="text-lg text-accent-blue-dark font-bold">
            {t(selectedField)}
          </h1>
          {selectedField === "adasEnable" &&
            <h2 className="text-sm text-gray-500 mt-0 mb-6">
              {t("adasEnable_trigger_title")}
            </h2>}
          {selectedField === "dmsEnable" &&
            <h2 className="text-sm text-gray-500 mt-0 mb-6">
              {t("dmsEnable_trigger_title")}
            </h2>}
        </div>

        {selectedField !== "dms_lensCoveredEnable" &&
          <>
            <div className="w-full space-y-2 mb-4">
              <h2 className="block font-semibold text-sm leading-none tracking-normal">
                {t("event_trigger")}
              </h2>
              <p className="font-normal text-sm leading-none mb-4 text-gray-500">
                {t("event_trigger_description")}
              </p>
            </div>
            <RangeSlider
              id={isMassConfigure ? "mass-sensitive" : sensitiveField}
              min={STEP_VALUES.min}
              max={STEP_VALUES.max}
              step={STEP_VALUES.step}
              value={
                isMassConfigure
                  ? massConfig?.sensitive ?? MASS_CONFIG_DEFAULTS.sensitive
                  : values[sensitiveField]
              }
              onChange={(e) => {
                const value = Number(e.target.value);

                if (isMassConfigure) {
                  updateMassConfig("sensitive", value);
                } else {
                  setFieldValue(sensitiveField, value);
                }
              }}
              stepLabels={STEP_LABELS_OPTIONS}
              isTopLabel={true}
            />
          </>}
      </div>

      <div className="col-span-6 p-6 border-b space-y-1">
        <div className="w-full space-y-2 mb-4">
          <h2 className="block font-semibold text-sm leading-none tracking-normal">
            {t("event_recording")}
          </h2>
          <p className="font-normal text-sm leading-none mb-4 text-gray-500">
            {t("event_recording_description")}
          </p>
        </div>
        <div className="col-span-6 grid grid-cols-12 gap-6">
          <AdminFormFieldInput
            type="number"
            label={t("pre_event_video_duration_sec")}
            name={isMassConfigure ? "mass-videoBefore" : videoBeforeField}
            id={isMassConfigure ? "mass-videoBefore" : videoBeforeField}
            value={
              isMassConfigure
                ? massConfig?.videoBefore
                : values[videoBeforeField]
            }
            onChange={
              isMassConfigure
                ? (e) => updateMassConfig("videoBefore", e.target.value)
                : handleChange
            }
            onBlur={(e) => {
              if (!isMassConfigure) {
                handleBlur(e);
              }
            }}
            error={getError("videoBefore", videoBeforeField)}
            touched={isMassConfigure ? true : touched[videoBeforeField]}
            customWrapperClass="col-span-6"
          />

          <AdminFormFieldInput
            type="number"
            label={t("post_event_video_duration_sec")}
            name={isMassConfigure ? "mass-videoAfter" : videoAfterField}
            id={isMassConfigure ? "mass-videoAfter" : videoAfterField}
            value={
              isMassConfigure
                ? massConfig?.videoAfter
                : values[videoAfterField]
            }
            onChange={
              isMassConfigure
                ? (e) => updateMassConfig("videoAfter", e.target.value)
                : handleChange
            }
            onBlur={(e) => {
              if (!isMassConfigure) {
                handleBlur(e);
              }
            }}
            error={getError("videoAfter", videoAfterField)}
            touched={isMassConfigure ? true : touched[videoAfterField]}
            customWrapperClass="col-span-6"
          />

          <AdminFormFieldInput
            type="number"
            label={t("event_debounce")}
            name={isMassConfigure ? "mass-eventDebounce" : eventDebounceField}
            id={isMassConfigure ? "mass-eventDebounce" : eventDebounceField}
            value={
              isMassConfigure
                ? massConfig?.eventDebounce
                : values[eventDebounceField]
            }
            onChange={
              isMassConfigure
                ? (e) => updateMassConfig("eventDebounce", e.target.value)
                : handleChange
            }
            onBlur={(e) => {
              if (!isMassConfigure) {
                handleBlur(e);
              }
            }}
            error={getError("eventDebounce", eventDebounceField)}
            touched={isMassConfigure ? true : touched[eventDebounceField]}
            customWrapperClass="col-span-6"
          />
        </div>
      </div>
      <div className="col-span-6 p-6 space-y-1 border-b">
        <h2 className="block font-semibold text-sm leading-none tracking-normal mb-4">
          {tMain("auto_upload")}
        </h2>
        <p className="font-normal text-sm leading-none mb-8 text-gray-500">
          {tMain("choose_upload_data")}
        </p>
        <AdminFormFieldDropdown
          label={tMain("media")}
          id={isMassConfigure ? "mass-mediaField" : mediaField}
          key={isMassConfigure ? "mass-mediaField" : mediaField}
          name={isMassConfigure ? "mass-mediaField" : mediaField}
          value={
            isMassConfigure
              ? massConfig?.media
              : values[mediaField]
                ? values[mediaField]
                : eventKey === "seatbeltUnfasten" ||
                  eventKey === "phone" ||
                  eventKey === "lensCovered" ||
                  eventKey === "smoking"
                  ? "snapshot"
                  : "video"
          }
          options={MEDIA_OPTIONS}
          onChange={(e) => {
            if (isMassConfigure) {
              updateMassConfig("media", e?.value);
            } else {
              setFieldValue(mediaField, e?.value);
            }
          }}
          onBlur={(e) => {
            if (!isMassConfigure) {
              setFieldTouched(mediaField, true);
              handleBlur(e);
            }
          }}
          error={isMassConfigure ? undefined : errors[mediaField]}
          touched={isMassConfigure ? false : touched[mediaField]}
          customWrapperClass="col-span-3 mt-6"
        />
      </div>
      <div className="col-span-6 p-6 space-y-1">
        <h2 className="block font-semibold text-sm leading-none tracking-normal mb-4">
          {t("alerts")}
        </h2>
        <AdminFormFieldCheckbox
          customWrapperClass="w-1/2"
          label={t("in_cabin_audio_alert")}
          id={isMassConfigure ? "mass-audioAlert" : audioAlertField}
          type="checkbox"
          name={isMassConfigure ? "mass-audioAlert" : audioAlertField}
          checked={
            isMassConfigure
              ? massConfig?.audioAlert
              : Boolean(values[audioAlertField])
          }
          onChange={(e) => {
            if (isMassConfigure) {
              updateMassConfig("audioAlert", e.target.checked);
            } else {
              handleChange(e);
            }
          }}
          onBlur={(e) => {
            if (!isMassConfigure) {
              handleBlur(e);
            }
          }}
          error={isMassConfigure ? undefined : errors[audioAlertField]}
          touched={isMassConfigure ? true : touched[audioAlertField]}
        />
      </div>
    </>
  );
};

export default EventField;
